/**
 * PaperDetailsScreen — loads real paper data from API
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  ArrowLeft, Bookmark, Share2, ExternalLink, Users,
  Sparkles, ChevronRight, Loader2,
} from 'lucide-react';
import { notify } from '../utils/notifications';
import { getQuickInsight } from '../services/aiService';
import { useApp } from '../context/AppContext';
import { useEventData } from '../context/EventDataContext';
import { useFavorites } from '../hooks/useFavorites';
import { CopilotFab } from '../components/CopilotFab';
import { AICopilotModal } from '../components/AICopilotModal';
import { extractInsightPreview } from '../utils/insightPreview';
import { previewPaperInsight } from '../preview/previewAI';
import { useEventModules } from '../hooks/useEventModules';
import { isPaperAIEnabled, type FeedbackAvailabilityState } from '@conference/shared';
import { sessionFeedbackService } from '../services/sessionFeedbackService';
import { API_URL } from '../services/apiClient';
import { ReportAIResponseButton } from '../components/ReportAIResponseButton';
import { PaperFigures } from '../components/PaperFigures';

// ─── Inline AI Insights Card ────────────────────────────────────────────────

type InsightState = 'idle' | 'loading' | 'loaded' | 'error';

interface AIInsightCardProps {
  paperId: string;
  paperTitle: string;
  paperAuthors?: string;
  paperVenue?: string;
  paperAbstract?: string;
  onAskAI: () => void;
  preview?: boolean;
}

function AIInsightCard({ paperId, paperTitle, paperAuthors, paperVenue, paperAbstract, onAskAI, preview }: AIInsightCardProps) {
  const { authSession, isAuthSessionCurrent } = useApp();
  const [state, setState] = useState<InsightState>('idle');
  const [insight, setInsight] = useState<string>('');
  const [insightReceiptId, setInsightReceiptId] = useState<string | undefined>();

  const loadInsight = async () => {
    if (preview) {
      setInsightReceiptId(undefined);
      setInsight(previewPaperInsight(paperTitle));
      setState('loaded');
      return;
    }
    const owner = authSession;
    if (!owner || !isAuthSessionCurrent(owner)) return;
    setState('loading');
    try {
      const result = await getQuickInsight({
        mode: 'paper-insight',
        paperId,
        paperTitle,
        paperAuthors,
        paperVenue,
        paperAbstract,
      }, undefined, owner.signal);
      if (!isAuthSessionCurrent(owner)) return;
      const preview = extractInsightPreview(result.content);
      const previewLines = [
        preview.summary,
        ...preview.takeaways.map((takeaway) => `- ${takeaway}`),
      ].filter(Boolean);
      setInsight(previewLines.join('\n') || result.content.slice(0, 200));
      setInsightReceiptId(result.receiptId);
      setState('loaded');
    } catch {
      if (isAuthSessionCurrent(owner)) setState('error');
    }
  };

  useEffect(() => {
    loadInsight();
  }, [authSession, preview]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="rounded-xl border overflow-hidden mb-6"
      style={{ background: 'var(--app-tag-bg)', borderColor: 'var(--app-tag-border)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--app-tag-border)' }}>
        <div className="flex items-center gap-2">
          <div
            className="size-6 rounded-full flex items-center justify-center"
            style={{ background: 'var(--app-accent-dim)' }}
          >
            <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--app-accent)' }} />
          </div>
          <span className="text-xs" style={{ color: 'var(--app-accent)' }}>AI Insights</span>
        </div>
        {state === 'loading' && (
          <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--app-accent)' }} />
        )}
      </div>

      {/* Body */}
      <div className="px-4 py-3">
        {state === 'loading' && (
          <div className="flex flex-col gap-2">
            {[60, 80, 50].map((w, i) => (
              <div
                key={i}
                className="h-2.5 rounded-full animate-pulse"
                style={{ width: `${w}%`, background: 'var(--app-accent-dim)' }}
              />
            ))}
          </div>
        )}

        {state === 'loaded' && (
          <div className="flex flex-col gap-1">
            {insight.split('\n').map((line, i) => (
              <p key={i} className="text-xs leading-relaxed" style={{ color: 'var(--app-text-subtle)' }}>
                {line.startsWith('- ') ? `• ${line.replace('- ', '')}` : line}
              </p>
            ))}
            <ReportAIResponseButton receiptId={insightReceiptId} />
          </div>
        )}

        {state === 'error' && (
          <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>
            Could not load insights. Tap "Ask AI" to explore this paper.
          </p>
        )}

        {state === 'idle' && (
          <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>
            Tap to generate AI insights for this paper.
          </p>
        )}
      </div>

      {/* CTA */}
      <button
        onClick={onAskAI}
        className="w-full flex items-center justify-between px-4 py-3 border-t transition-colors active:opacity-70"
        style={{ borderColor: 'var(--app-tag-border)' }}
      >
        <span className="text-xs" style={{ color: 'var(--app-accent)' }}>
          Ask AI about this paper
        </span>
        <ChevronRight className="w-3.5 h-3.5" style={{ color: 'var(--app-accent)' }} />
      </button>
    </div>
  );
}

// ─── Main Screen ─────────────────────────────────────────────────────────────

export default function PaperDetailsScreen() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { aiPreferences, authSession, currentEventId, isAuthSessionCurrent, selectedEvent } = useApp();
  const { isEnabled } = useEventModules();
  const { preview, services: { paperService } } = useEventData();
  const aiEnabled = isEnabled('ai_copilot');
  const paperAIEnabled = aiEnabled && isPaperAIEnabled(selectedEvent?.app_modules);
  const { isFavorite, toggleFavorite } = useFavorites();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [paper, setPaper] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [presentationFeedbackState, setPresentationFeedbackState] = useState<FeedbackAvailabilityState | null>(null);
  const [previewCopilotOpen, setPreviewCopilotOpen] = useState(false);

  const saved = id ? isFavorite(id, 'paper') : false;

  useEffect(() => {
    if (!id || !currentEventId) { setNotFound(true); setLoading(false); return; }
    const owner = authSession;
    if (!preview && (!owner || !isAuthSessionCurrent(owner))) return;
    const isCurrent = () => preview || (!!owner && isAuthSessionCurrent(owner));
    let disposed = false;
    paperService.getById(id, currentEventId, preview ? undefined : { signal: owner!.signal })
      .then(p => {
        if (disposed || !isCurrent()) return;
        if (!p) setNotFound(true);
        else setPaper(p);
      })
      .catch(() => {
        if (!disposed && isCurrent()) setNotFound(true);
      })
      .finally(() => {
        if (!disposed && isCurrent()) setLoading(false);
      });
    return () => { disposed = true; };
  }, [authSession, currentEventId, id, isAuthSessionCurrent, preview, paperService]);

  useEffect(() => {
    if (preview) { setPresentationFeedbackState('unavailable'); return; }
    if (!id || !paper?.session_id) { setPresentationFeedbackState(null); return; }
    const owner = authSession;
    if (!owner || !isAuthSessionCurrent(owner)) return;
    let disposed = false;
    sessionFeedbackService.getPresentationForm(id, owner.signal)
      .then((form) => { if (!disposed && isAuthSessionCurrent(owner)) setPresentationFeedbackState(form.state); })
      .catch(() => { if (!disposed && isAuthSessionCurrent(owner)) setPresentationFeedbackState('unavailable'); });
    return () => { disposed = true; };
  }, [authSession, id, isAuthSessionCurrent, paper?.session_id, preview]);

  const handleSave = () => {
    if (!id) return;
    const wasSaved = isFavorite(id, 'paper');
    toggleFavorite(id, 'paper');
    if (!wasSaved) notify.custom.paperSaved();
    else notify.info('Removed from library');
  };

  const handleAskAI = () => {
    if (!paper) return;
    if (preview) {
      setPreviewCopilotOpen(true);
      return;
    }
    const params = new URLSearchParams({
      mode: 'paper-insight',
      paperId: paper.id,
      paperTitle: paper.title,
      paperAuthors: (paper.authors as string[]).join(', '),
      ...(paper.conference ? { paperVenue: paper.conference } : {}),
      ...(paper.abstract ? { paperAbstract: paper.abstract } : {}),
    });
    navigate(`/report-builder?${params.toString()}`);
  };

  if (loading) {
    return (
      <div className="size-full flex items-center justify-center" style={{ background: 'var(--app-bg)' }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--app-accent)' }} />
      </div>
    );
  }

  if (notFound || !paper) {
    return (
      <div className="size-full flex flex-col" style={{ background: 'var(--app-bg)' }}>
        <div className="app-screen-header flex items-center px-4 pt-12 pb-4">
          <button
            onClick={() => navigate(-1)}
            className="size-10 rounded-full flex items-center justify-center"
            style={{ background: 'var(--app-surface)' }}
          >
            <ArrowLeft className="w-5 h-5" style={{ color: 'var(--app-text)' }} />
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>Paper not found.</p>
        </div>
      </div>
    );
  }

  const keywords: string[] = paper.keywords ?? [];
  const authors: string[] = paper.authors ?? [];

  return (
    <div className="size-full flex flex-col overflow-hidden" style={{ background: 'var(--app-bg)' }}>
      {/* Header */}
      <div className="app-screen-header flex items-center justify-between px-4 pt-12 pb-4 shrink-0" style={{ background: 'var(--app-bg)' }}>
        <button
          aria-label="Back"
          onClick={() => navigate(-1)}
          className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
          style={{ background: 'var(--app-surface)' }}
        >
          <ArrowLeft className="w-5 h-5" style={{ color: 'var(--app-text)' }} />
        </button>
        {!preview && <div className="flex gap-2">
          <button
            onClick={() => notify.success('Link copied to clipboard')}
            className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ background: 'var(--app-surface)' }}
          >
            <Share2 className="w-4 h-4" style={{ color: 'var(--app-text-subtle)' }} />
          </button>
          <button
            onClick={handleSave}
            className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ background: 'var(--app-surface)' }}
          >
            <Bookmark
              className="w-4 h-4"
              style={saved ? { fill: 'var(--app-accent)', color: 'var(--app-accent)' } : { color: 'var(--app-text-subtle)' }}
            />
          </button>
        </div>}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 pb-8">
        {/* Category / conference badge */}
        {paper.conference && (
          <span
            className="text-xs px-3 py-1 rounded-full"
            style={{ background: 'var(--app-tag-bg)', color: 'var(--app-accent)' }}
          >
            {paper.conference}
          </span>
        )}

        {/* Title */}
        <h1 className="text-[var(--app-text)] text-xl leading-snug mt-3 mb-4">{paper.title}</h1>

        {/* Year */}
        {paper.year && (
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs" style={{ color: 'var(--app-text-muted)' }}>{paper.year}</span>
            {paper.doi && (
              <span className="text-xs" style={{ color: 'var(--app-text-muted)' }}>DOI: {paper.doi}</span>
            )}
          </div>
        )}

        {/* Authors */}
        {authors.length > 0 && (
          <div className="mb-5">
            <h2 className="text-sm mb-2 flex items-center gap-1.5" style={{ color: 'var(--app-text)' }}>
              <Users className="w-4 h-4" style={{ color: 'var(--app-accent)' }} />
              Authors
            </h2>
            <div className="flex flex-wrap gap-2">
              {authors.map(author => (
                <span
                  key={author}
                  className="text-xs px-3 py-1.5 rounded-lg border"
                  style={{ background: 'var(--app-surface)', color: 'var(--app-text-subtle)', borderColor: 'var(--app-border)' }}
                >
                  {author}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Abstract */}
        {paper.abstract && (
          <div className="mb-5">
            <h2 className="text-base mb-2" style={{ color: 'var(--app-text)' }}>Abstract</h2>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--app-text-muted)' }}>{paper.abstract}</p>
          </div>
        )}

        {/* Keywords */}
        {keywords.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm mb-2" style={{ color: 'var(--app-text)' }}>Keywords</h2>
            <div className="flex flex-wrap gap-2">
              {keywords.map(kw => (
                <span
                  key={kw}
                  className="text-xs px-2 py-1 rounded-full border"
                  style={{ background: 'var(--app-tag-bg)', borderColor: 'var(--app-tag-border)', color: 'var(--app-accent)' }}
                >
                  {kw}
                </span>
              ))}
            </div>
          </div>
        )}

        <PaperFigures key={`${currentEventId}:${paper.id}`} figures={paper.figures ?? []} paperId={paper.id} eventId={currentEventId!} preview={preview} />

        {/* ── AI Insights Card ── */}
        {paperAIEnabled && aiPreferences.aiAutoInsights && (
          <AIInsightCard
            paperId={paper.id}
            paperTitle={paper.title}
            paperAuthors={authors.join(', ')}
            paperVenue={paper.conference}
            paperAbstract={paper.abstract}
            onAskAI={handleAskAI}
            preview={preview}
          />
        )}

        {/* Actions */}
        {!preview && <div className="flex gap-3">
          {paper.session_id && (presentationFeedbackState === 'open' || presentationFeedbackState === 'upcoming') && (
            <button onClick={() => navigate(`/paper/${paper.id}/feedback`)} className="flex-1 h-12 rounded-xl flex items-center justify-center text-sm border" style={{ background: 'var(--app-tag-bg)', color: 'var(--app-accent)', borderColor: 'var(--app-tag-border)' }}>
              Give feedback
            </button>
          )}
          <button
            onClick={handleSave}
            className="flex-1 h-12 rounded-xl flex items-center justify-center gap-2 text-sm border transition-colors active:scale-[0.98]"
            style={
              saved
                ? { background: 'var(--app-accent-solid)', color: '#fff', borderColor: 'var(--app-accent-solid)' }
                : { background: 'var(--app-surface)', color: 'var(--app-text)', borderColor: 'var(--app-border)' }
            }
          >
            <Bookmark className={`w-4 h-4 ${saved ? 'fill-white' : ''}`} />
            {saved ? 'Saved' : 'Save Paper'}
          </button>
          {paper.pdf_url && (
            <button
              onClick={() => {
                const raw = paper.pdf_url!;
                const url = raw.startsWith('http://') || raw.startsWith('https://')
                  ? raw
                  : raw.startsWith('/')
                  ? `${API_URL}${raw}`
                  : `${API_URL}/files/${raw}`;
                window.open(url, '_blank');
              }}
              className="flex-1 h-12 rounded-xl flex items-center justify-center gap-2 text-sm border active:scale-[0.98] transition-transform"
              style={{ background: 'var(--app-surface)', color: 'var(--app-text)', borderColor: 'var(--app-border)' }}
            >
              <ExternalLink className="w-4 h-4" />
              View PDF
            </button>
          )}
        </div>}

        {/* Ask AI full-width CTA */}
        {paperAIEnabled && (
        <button
          onClick={handleAskAI}
          className="w-full mt-3 h-12 rounded-xl flex items-center justify-center gap-2 text-sm border active:scale-[0.98] transition-transform"
          style={{ background: 'var(--app-tag-bg)', borderColor: 'var(--app-tag-border)', color: 'var(--app-accent)' }}
        >
          <Sparkles className="w-4 h-4" />
          Ask AI about this paper
        </button>
        )}
      </div>
      {!preview && <CopilotFab />}
      {preview && <AICopilotModal isOpen={previewCopilotOpen} onClose={() => setPreviewCopilotOpen(false)} />}
    </div>
  );
}
